import GlobeMapIcon from '@ubnt/icons/GlobeMapIcon';
import Checkbox from '@ubnt/ui-components/Checkbox/Checkbox';
import Dropdown from '@ubnt/ui-components/Dropdown/Dropdown';
import { DropdownOption, DropdownValue } from '@ubnt/ui-components/Dropdown/types';
import { memo, useCallback, useMemo } from 'react';
import { getCountriesOptions } from '@ubnt/wifi-calculator/utils/regulatoryLimits';

import SettingsTile from './SettingsTile';

import classes from './CountryTile.module.scss';

interface CountryTileProps {
  country: string;
  isUserLocation: boolean;
  onCountryChange: (newValue: string) => void;
  onUseUserLocation: () => void;
  userLocationSelectable?: boolean;
}

const COUNTRY_INFO = (
  <div>
    <span>
      Country selection impacts the available channel list, bandwidth, and power limits according to the regulatory
      rules of the chosen country.
    </span>
    <br />
    <span>This helps to simulate WiFi calculations more accurately.</span>
  </div>
);

const searchableProps = {
  keys: ['label', 'value'],
};

const CountryTileImpl = (props: CountryTileProps) => {
  const { country, isUserLocation, onCountryChange, onUseUserLocation, userLocationSelectable } = props;

  const countryOptions = useMemo(() => getCountriesOptions(), []);

  const onChange = useCallback(
    (_: DropdownOption, newValue: DropdownValue<false>) => {
      onCountryChange(String(newValue));
    },
    [onCountryChange]
  );

  const onCheckedChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onUseUserLocation();
      }
    },
    [onUseUserLocation]
  );

  return (
    <SettingsTile icon={GlobeMapIcon} info={COUNTRY_INFO} title="Country">
      <Checkbox
        checked={isUserLocation}
        disabled={!userLocationSelectable}
        id="userLocationCheckbox"
        onChange={onCheckedChange}
      >
        Use Your Location
      </Checkbox>
      <Dropdown
        label="Select Country"
        onChange={onChange}
        optionContainerClassName={classes.optionContainer}
        options={countryOptions}
        value={country}
        searchableProps={searchableProps}
        variant="secondary"
        width="100%"
        portal
        searchable
      />
    </SettingsTile>
  );
};

const CountryTile = memo(CountryTileImpl);

export default CountryTile;
